/**
 * AutoUpdateManager - Automatic cache clearing and version management
 *
 * Automatically clears cache and reloads when:
 * 1. New version is detected (based on build timestamp)
 * 2. First launch after installation
 * 3. Service worker cache exists from previous version
 *
 * Prevents infinite reload loops with session-based tracking.
 */

export class AutoUpdateManager {
  private static readonly VERSION_KEY = 'blobo-app-version';
  private static readonly RELOAD_SESSION_KEY = 'blobo-reload-attempted';

  /**
   * Check for updates and handle cache clearing automatically
   * Call this as early as possible in app initialization
   */
  public static async checkAndUpdate(): Promise<void> {
    console.log('[AutoUpdate] Checking for updates...');

    // Get current build version (using build timestamp)
    const currentVersion = this.getCurrentVersion();
    const storedVersion = localStorage.getItem(this.VERSION_KEY);

    // Check if we already attempted reload this session (prevent infinite loops)
    const reloadAttempted = sessionStorage.getItem(this.RELOAD_SESSION_KEY);

    if (reloadAttempted === 'true') {
      console.log('[AutoUpdate] Already reloaded this session, skipping');
      // Store current version for next time
      localStorage.setItem(this.VERSION_KEY, currentVersion);
      return;
    }

    // Determine if update is needed
    const needsUpdate = !storedVersion || storedVersion !== currentVersion;

    if (needsUpdate) {
      console.log(
        `[AutoUpdate] Version mismatch detected! Stored: ${storedVersion}, Current: ${currentVersion}`
      );
      await this.clearCacheAndReload();
    } else {
      console.log('[AutoUpdate] Version up-to-date:', currentVersion);
      // Even if version matches, check if service worker cache exists and clear it
      // This handles cases where cache wasn't properly cleared before
      const cacheCleared = await this.clearServiceWorkerCacheIfExists();
      if (cacheCleared) {
        console.log('[AutoUpdate] Found and cleared stale service worker cache');
      }
    }
  }

  /**
   * Get current app version (using build timestamp to ensure uniqueness)
   */
  private static getCurrentVersion(): string {
    // Use a combination of package version and build timestamp
    // The timestamp ensures each build gets a unique version identifier
    const packageVersion = '0.9.7'; // From package.json
    const buildTimestamp =
      document.querySelector('meta[name="build-time"]')?.getAttribute('content') ||
      Date.now().toString();
    return `${packageVersion}-${buildTimestamp}`;
  }

  /**
   * Clear all caches and reload the app
   */
  private static async clearCacheAndReload(): Promise<void> {
    console.log('[AutoUpdate] 🔄 Clearing cache and reloading...');

    // Mark that we're attempting a reload (prevents infinite loops)
    sessionStorage.setItem(this.RELOAD_SESSION_KEY, 'true');

    try {
      // 1. Clear all cache storage
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        console.log('[AutoUpdate] ✅ Cleared cache storage:', cacheNames);
      }

      // NOTE: DO NOT unregister service workers here!
      // VitePWA will handle service worker updates automatically
      // Unregistering causes offline mode to break

      // 2. Store new version before reload
      const currentVersion = this.getCurrentVersion();
      localStorage.setItem(this.VERSION_KEY, currentVersion);

      // 3. Force hard reload (bypass cache)
      console.log('[AutoUpdate] 🔄 Reloading page...');
      window.location.reload();
    } catch (error) {
      console.error('[AutoUpdate] ❌ Error during update:', error);
      // Reload anyway even if cache clearing fails
      window.location.reload();
    }
  }

  /**
   * Check if service worker cache exists and clear it
   * Returns true if cache was found and cleared
   */
  private static async clearServiceWorkerCacheIfExists(): Promise<boolean> {
    // Skip this check - let VitePWA handle service worker caching
    // Aggressive cache clearing breaks offline mode
    return false;
  }

  /**
   * Reset the reload session flag (for testing purposes)
   */
  public static resetReloadFlag(): void {
    sessionStorage.removeItem(this.RELOAD_SESSION_KEY);
  }
}
